import React, { useState, useEffect, useContext } from 'react';
import LogoImage from '../assets/logo/IconOnly_Transparent_NoBuffer.png';
import { Badge } from '@heroui/react';
import { UserContext } from '../context/UserContext';
import { Link, useLocation } from 'react-router-dom';
import AccountMenu from './AccountMenu';

// Simple SVG Icons
const HamburgerIcon = ({ size = 36 }) => (
  <svg width={size} height={size} viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 27H30M6 18H30M6 9H30" stroke="#0C3058" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Mock environment config - replace with your actual env import
const env = { APP_ENV: 'production' }; // change to 'development' or 'staging' to test

// User initials calculation function
const getUserInitials = (userName) => {
  if (!userName || typeof userName !== 'string') {
    return 'U'; // Default fallback
  }
  
  const nameParts = userName.trim().split(' ');
  
  if (nameParts.length > 1) {
    // Take first 2 letters from first 2 words
    const firstInitial = nameParts[0].charAt(0).toUpperCase();
    const secondInitial = nameParts[1].charAt(0).toUpperCase();
    return firstInitial + secondInitial;
  } else {
    // Take first 2 characters from single word
    return nameParts[0].substring(0, 2).toUpperCase();
  }
};

// User Avatar Component
const UserAvatar = ({ userName, userEmail, size = 40 }) => {
  const initials = getUserInitials(userName);
  const [showTooltip, setShowTooltip] = useState(false);
  
  return (
    <div 
      style={{
        position: 'relative',
        display: 'inline-block'
      }}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div style={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: '#D9D9D9',
        color: '#0d3259',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.4,
        fontWeight: 'bold',
        fontFamily: '"Rubik", sans-serif',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        border: '2px solid #ffffff',
        cursor: 'pointer',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'scale(1.05)';
        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
      }}
      >
        {initials}
      </div>
      
      {/* Tooltip */}
      {showTooltip && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '100%',
          transform: 'translateY(-50%)',
          marginLeft: '12px',
          padding: '8px 12px',
          backgroundColor: '#0d3259',
          color: 'white',
          fontSize: '13px',
          borderRadius: '6px',
          zIndex: 1000,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          fontFamily: '"Rubik", sans-serif',
          minWidth: '150px'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
            {userName}
          </div>
          {userEmail && (
            <div style={{ 
              fontSize: '12px', 
              opacity: 0.9,
              fontWeight: 'normal'
            }}>
              {userEmail}
            </div>
          )}
          {/* Tooltip arrow */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '-6px',
            transform: 'translateY(-50%)',
            width: 0,
            height: 0,
            borderTop: '6px solid transparent',
            borderBottom: '6px solid transparent',
            borderRight: '6px solid #2D3748'
          }}></div>
        </div>
      )}
    </div>
  );
};

const getEnvironmentConfig = () => {
  const configs = {
    development: {
      headerStyle: { backgroundColor: '#ffffff' },
      badgeStyle: { 
        backgroundColor: '#4CAF50',
        color: 'white',
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '11px',
        fontWeight: 'bold'
      },
      envLabel: 'DEV',
    },
    staging: {
      headerStyle: { backgroundColor: '#ffffff' },
      badgeStyle: { 
        backgroundColor: '#FF9800',
        color: 'white',
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '11px',
        fontWeight: 'bold'
      },
      envLabel: 'STAGING',
    },
    production: {
      headerStyle: { backgroundColor: '#ffffff' },
      badgeStyle: null,
      envLabel: '',
    },
  };

  return configs[env.APP_ENV] || configs.production;
};

// Text Component - Default text display
const HeaderText = React.memo(({ text, isMobile }) => {
  const defaultDesktopText = "mashlom.me - כלי עזר לצוותי רפואה";
  
  if (isMobile) {
    return text && text !== "" ? (
      <span style={{
        fontSize: '1.5rem',
        fontWeight: '500',
        color: '#2C5282',
        fontFamily: '"Rubik", sans-serif'
      }}>
        {text}
      </span>
    ) : null;
  }
  
  return (
    <span style={{
      fontSize: '1.5rem',
      fontWeight: '500',
      color: '#2C5282',
      fontFamily: '"Rubik", sans-serif'
    }}>
      {text || defaultDesktopText}
    </span>
  );
});

// Placeholder logos - replace with actual images
const MashlomLogo = () => (
  <img style={{
    height: '30px',
    width: 'auto',
    objectFit: 'contain',
    borderRadius: '8px',
    paddingRight: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: 'bold',
    color: 'white'
  }} src={LogoImage} alt="Mashlom Logo" />
);

const AdditionalLogo = () => (
  <div style={{
    width: 'auto',
    height: '30px',
    background: 'linear-gradient(135deg, #10B981 0%, #84CC16 100%)',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    fontWeight: 'bold',
    color: 'white'
  }}>
    E
  </div>
);

// Main Header Component
const Header = ({
  additionalLogo = null,
  text = null,
  customComponent = null,
  onMenuOpen = () => {}
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const { user, pendingRequests } = useContext(UserContext);
  const envConfig = React.useMemo(() => getEnvironmentConfig(), []);
  // Onboarding has no sidebar and no profile page to reach, so the avatar opens
  // an account menu (sign out) instead, on mobile too.
  const isOnboarding = useLocation().pathname.startsWith('/onboarding');

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const getPendingRequestsCount = () => {
    if (!user || !pendingRequests) return 0;
    
    if (user.isSuperAdmin) {
      // Super admin can see all pending requests
      return pendingRequests.filter(req => req.type === 'ask-to-join-to-department').length;
    }
    
    // Regular admin can only see requests for their departments
    const adminDepartments = user.roles
      .filter(role => role.role === 'ADMIN')
      .map(role => role.department);
    
    return pendingRequests.filter(req => 
      req.type === 'ask-to-join-to-department' && 
      adminDepartments.includes(req.department?._id || req.department)
    ).length;
  };

  const headerStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    direction: 'ltr',
    height: isMobile ? '50px' : '70px',
    zIndex: 1000,
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    ...envConfig.headerStyle
  };

  const containerStyle = {
    height: '100%',
    padding: '0 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  };

  return (
    <header style={headerStyle}>
      <div style={containerStyle}>
        {isMobile ? (
          // Mobile Layout
          <>
            {/* Logos on the left */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <MashlomLogo />
              {additionalLogo && (
                <>
                  <div style={{ 
                    width: '1px', 
                    height: '24px', 
                    backgroundColor: '#CBD5E0'
                  }}></div>
                  {typeof additionalLogo === 'string' ? (
                    <img src={additionalLogo} alt="Additional Logo" style={{ height: '40px', width: 'auto' }} />
                  ) : additionalLogo === true ? (
                    <AdditionalLogo />
                  ) : (
                    additionalLogo
                  )}
                </>
              )}
              {envConfig.envLabel && (
                <div style={envConfig.badgeStyle}>
                  {envConfig.envLabel}
                </div>
              )}
            </div>

            {/* Center area - custom component or text */}
            <div style={{ 
              flex: 1, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              padding: '0 10px'
            }}>
              {customComponent || <HeaderText text={text} isMobile={isMobile} />}
            </div>

            {/* Hamburger menu on the right */}
            <div>
              {user && isOnboarding ? (
                <AccountMenu>
                  <UserAvatar userName={user.name} userEmail={user.email} size={36} />
                </AccountMenu>
              ) : user && (user.isSuperAdmin || user.roles.some(role => role.role === 'ADMIN')) && getPendingRequestsCount() > 0 ? (
                <Badge 
                  content={getPendingRequestsCount()} 
                  color="danger" 
                  size="sm"
                  placement="top-right"
                  style={{
                    transform: 'translate(-12px, 12px)'
                  }}
                >
                  <button 
                    onClick={onMenuOpen}
                    style={{
                      padding: '8px',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#2C5282'
                    }}
                  >
                    <HamburgerIcon size={36} />
                  </button>
                </Badge>
              ) : (
                <button 
                  onClick={onMenuOpen}
                  style={{
                    padding: '8px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#2C5282'
                  }}
                >
                  <HamburgerIcon size={36} />
                </button>
              )}
            </div>
          </>
        ) : (
          // Desktop Layout
          <>
            {/* User Avatar on the left - only show if user exists */}
            {user && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                marginRight: '20px'
              }}>
                {isOnboarding ? (
                  <AccountMenu>
                    <UserAvatar userName={user.name} userEmail={user.email} size={46} />
                  </AccountMenu>
                ) : (
                  <Link
                    to="/profile">
                    <UserAvatar userName={user.name} userEmail={user.email} size={46} />
                  </Link>
                )}
              </div>
            )}
            
            {/* Logos on the right */}
            <div style={{ 
              flex: 1,
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'flex-end',
              gap: '10px' 
            }}>
              {/* Environment badge */}
              {envConfig.envLabel && (
                <div style={{ ...envConfig.badgeStyle, marginRight: 'auto' }}>
                  {envConfig.envLabel}
                </div>
              )}
              
              {/* Text aligned closer to the right side, before logos */}
              <div style={{ 
                flex: 1, 
                textAlign: 'right',
                paddingRight: '20px',
                direction: 'rtl'
              }}>
                {customComponent || <HeaderText text={text} isMobile={isMobile} />}
              </div>
              
              {/* Logos */}
              {additionalLogo && (
                <>
                  {typeof additionalLogo === 'string' ? (
                    <img src={additionalLogo} alt="Additional Logo" style={{ height: '40px', width: 'auto' }} />
                  ) : additionalLogo === true ? (
                    <AdditionalLogo />
                  ) : (
                    additionalLogo
                  )}
                  <div style={{ 
                    width: '1px', 
                    height: '24px', 
                    backgroundColor: '#CBD5E0'
                  }}></div>
                </>
              )}
              <MashlomLogo />
            </div>
          </>
        )}
      </div>
    </header>
  );
};

export default Header;